import { useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { trackSignInPromptShown, trackSignInClicked } from "@/analytics";
import type { ChatError } from "@/hooks/useChat";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { formatBillingDate } from "@/lib/format";
import { isPaperThemeActive } from "@/hooks/useThemeFlag";
import { PaperStatusChip } from "@/components/paper/PaperStatusChip";
import { BillingLinkButton } from "../buttons/BillingLinkButton";

const InsufficientCreditsMessage = ({ forceAnon = false }: { forceAnon?: boolean }) => {
  const { isAuthenticated: realIsAuthenticated, signIn } = useAuth();
  const isAuthenticated = forceAnon ? false : realIsAuthenticated;
  const { currentPlanId, isActive, currentPeriodEnd } = useSubscription();

  const linkClass = isPaperThemeActive()
    ? "underline text-[var(--periwinkle)] hover:text-[var(--periwinkle-bold)] cursor-pointer"
    : "underline hover:text-red-300 cursor-pointer";

  const prefix = "You have exceeded your plan's usage limit.";

  useEffect(() => {
    if (!isAuthenticated) {
      trackSignInPromptShown('insufficient_credits');
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <span>
        {prefix}{" "}
        <a
          onClick={() => { trackSignInClicked('insufficient_credits'); signIn(); }}
          className={linkClass}
        >
          Log in to continue for free.
        </a>
      </span>
    );
  }

  if (!isActive || currentPlanId === "free") {
    return (
      <span>
        {prefix}{" "}
        <BillingLinkButton>
          Subscribe to continue.
        </BillingLinkButton>
      </span>
    );
  }

  if (currentPlanId === "tier3") {
    const renewDate = currentPeriodEnd
      ? formatBillingDate(currentPeriodEnd)
      : null;
    return (
      <span>
        {prefix} Your subscription will renew
        {renewDate ? ` on ${renewDate}` : " soon"}.{" "}
        <a
          href="mailto:hello@symbolica.ai"
          className={linkClass}
        >
          Contact hello@symbolica.ai
        </a>{" "}
        to continue.
      </span>
    );
  }

  const resetDate = currentPeriodEnd
    ? formatBillingDate(currentPeriodEnd)
    : null;

  return (
    <span>
      {prefix}{" "}
      <BillingLinkButton>
        {resetDate
          ? `Your plan will reset on ${resetDate}, or you can upgrade to continue.`
          : "Your plan will reset soon, or you can upgrade to continue."}
      </BillingLinkButton>
    </span>
  );
};

export const ChatErrorBanner = ({
  error,
  forceAnon = false,
}: {
  error?: ChatError | null;
  forceAnon?: boolean;
}) => {
  if (!error) return null;
  const body = error.type === "payment-required"
    ? <InsufficientCreditsMessage forceAnon={forceAnon} />
    : "Something went wrong. Please try again.";
  if (isPaperThemeActive()) {
    return (
      <PaperStatusChip
        leading={<AlertCircle className="h-4 w-4 text-[var(--fg-3)]" />}
        multiline
      >
        {body}
      </PaperStatusChip>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="flex max-w-[85%] items-start gap-2 rounded-2xl border border-red-500/20 bg-red-500/5 px-4 py-3">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
        <span className="text-[13px] text-red-400">{body}</span>
      </div>
    </div>
  );
};
